import { Tooltip } from 'antd';
import classNames from 'classnames';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { CURRENCY_FORMAT, TOOLTIP_PLACEMENT, UNIT_MEASURE } from '@common/constants';
import cssColors from '@common/css/cssColors.module.scss';
import formatUOM from '@common/helpers/formatUOM';
import { getOnTrendLabelFromProduct } from '@common/helpers/onTrendLabel';
import { ApplianceModel, ApplianceType, CutoutLaborModel } from '@common/models';
import { getProductDescription } from '@common/utils';
import { formatPriceUnitOrFree } from '@common/utils/formatPriceUnitOrFree';
import ApplianceDragableImage from '@core/components/ApplianceDragableImage/ApplianceDragableImage';
import InstructionsBanner from '@core/components/instructions-banner/InstructionsBanner';
import ImageUtils from '@core/utils/ImageUtils';
import { useApplianceContext } from '@ee-context';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { selectSelectedSink, updateSelectedSink } from '@redux/slices/sinkSlice';

import './sink.scss';

interface SinkItemProps {
    sink: ApplianceModel;
    cutout: CutoutLaborModel | undefined; // cutout that matches the sink
}

export const SinkItem = ({ sink, cutout }: SinkItemProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { updateIsDraggingAppliance } = useApplianceContext();
    const description = getProductDescription(sink);
    const selectedSink = useAppSelector(selectSelectedSink);

    useEffect(() => {
        return () => {
            updateIsDraggingAppliance(false);
        };
    }, [updateIsDraggingAppliance]);

    const onTrendLabel = getOnTrendLabelFromProduct(sink);

    // only do cutoutLabor if it's a real sink
    const isRealSink = sink.applianceType === ApplianceType.SINK;

    const isSelected = sink.uuid === selectedSink?.uuid;
    const unitOfMeasure = formatUOM(sink.unitOfMeasure || UNIT_MEASURE.SINK);

    return (
        <div
            className={classNames('card-option', { selected: isSelected })}
            onMouseUp={() => {
                dispatch(updateSelectedSink(sink));
            }}
        >
            {onTrendLabel ? <div className="on-trend">{onTrendLabel}</div> : null}
            <ApplianceDragableImage
                alt="sink"
                className="card-option__image"
                src={ImageUtils.imgUrl(sink.beautyShotImageUrl || sink.imageUrl, 300)}
                applianceModel={sink}
            />
            <InstructionsBanner className="card-option__banner" hidden={!isSelected} />
            <Tooltip title={description} color={cssColors.toolTipColor} placement={TOOLTIP_PLACEMENT}>
                <div className="name">{description}</div>
            </Tooltip>
            <div className={'bottom-area'}>
                {sink.discountPercentage && (
                    <div className="card-sale-tag">
                        <img src={'./public/assets/images/Tags/sale-tag.svg'} alt="sale-tag" />
                    </div>
                )}
                <div className="price">
                    {formatPriceUnitOrFree(sink.price, unitOfMeasure)}
                    {sink.discountPercentage && (
                        <div className="price-container-sink">
                            <div className="price-cell">{CURRENCY_FORMAT.format(sink.priceWithoutDiscount)}</div>
                            <div className="percentage-cell">{t('PRICE.OFF', { value: sink.discountPercentage })}</div>
                        </div>
                    )}
                </div>
            </div>
            {cutout && isRealSink && isSelected ? (
                <div className="card-option__cutout-charge">
                    {t('SCROLLABLE_AREA.COOKINGAPPLIANCE.CUTOUT_CHARGE_INFO', {
                        name:
                            getProductDescription(cutout, true) ||
                            t('SCROLLABLE_AREA.COOKINGAPPLIANCE.CUTOUT_CHARGE_GENERIC_NAME'),
                        price: formatPriceUnitOrFree(cutout.price),
                    })}
                </div>
            ) : null}
        </div>
    );
};
